import React, { useState, useEffect } from "react";
import { Settings, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Capacitor } from "@capacitor/core";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface GameSettingsMenuProps {
  className?: string;
  buttonSize?: 'sm' | 'md' | 'lg';
  align?: 'start' | 'center' | 'end';
}

export function GameSettingsMenu({ 
  className, 
  buttonSize = 'md', 
  align = 'end'
}: GameSettingsMenuProps) {
  // State to track if all game audio is muted
  const [isAudioMuted, setIsAudioMuted] = useState<boolean>(false);
  // Check if we're running on a mobile device
  const isNativePlatform = Capacitor.isNativePlatform();
  
  // Load muted state from localStorage on component mount
  useEffect(() => {
    // Load from sound-effects-muted for UI state
    const soundMuted = localStorage.getItem('sound-effects-muted');
    setIsAudioMuted(soundMuted === 'true');
  }, []);
  
  // Toggle all game audio function
  const toggleGameAudio = () => {
    const newMutedState = !isAudioMuted;
    setIsAudioMuted(newMutedState);
    
    // Apply the new mute state immediately
    console.log(`Setting all game audio muted state: ${newMutedState}`);
    
    // Import and use the control functions to mute/unmute all audio
    import('@/utils/sound-utils').then(({ setGameSoundsMuted, setBackgroundMusicMuted }) => {
      // Mute/unmute both game sounds and background music
      // The functions will handle updating localStorage
      setGameSoundsMuted(newMutedState);
      setBackgroundMusicMuted(newMutedState);
      
      // Play a sound to confirm the change if unmuting
      if (!newMutedState) {
        import('@/services/app-sound-service').then(({ default: appSoundService }) => {
          setTimeout(() => {
            appSoundService.playButtonSound();
          }, 100); // Slight delay to ensure unmute takes effect first
        });
      }
    });
  };
  
  // Button size based on prop
  const buttonSizeClass = {
    sm: "w-7 h-7",
    md: "w-8 h-8",
    lg: "w-9 h-9"
  }[buttonSize];
  
  // Icon size based on button size
  const iconSizeClass = {
    sm: "h-3 w-3",
    md: "h-4 w-4", 
    lg: "h-5 w-5"
  }[buttonSize];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "rounded-full bg-black/50 hover:bg-black/70 text-white border border-primary/30",
            buttonSizeClass,
            className
          )}
        >
          <Settings className={iconSizeClass} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align={align} 
        className="bg-black/90 border border-primary/40 text-white rounded-lg shadow-lg"
      >
        {/* Game audio toggle shown on all platforms */}
        
          <DropdownMenuItem 
            onClick={toggleGameAudio} 
            className="cursor-pointer flex items-center hover:bg-primary/20 focus:bg-primary/20"
          >
            {isAudioMuted ? (
              <>
                <VolumeX className="h-4 w-4 mr-2 text-white" />
                <span className="font-medium">Sound Off</span>
              </>
            ) : (
              <>
                <Volume2 className="h-4 w-4 mr-2 text-primary" />
                <span className="text-primary font-medium">Sound On</span>
              </>
            )}
          </DropdownMenuItem>
        
        
        {/* Additional menu items can be added here */}
        {/* For example:
        <DropdownMenuSeparator className="bg-primary/20" />
        <DropdownMenuItem className="cursor-pointer flex items-center hover:bg-primary/20 focus:bg-primary/20">
          <Icon className="h-4 w-4 mr-2" />
          <span className="font-medium">Menu Item</span>
        </DropdownMenuItem> 
        */}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
